import type { FetchStatus } from "@/base/types"
import type { AuthError, AuthErrorCode } from "@/constants/errors"
import type * as Models from "@/redux/models"
import type { Reducer } from "@reduxjs/toolkit"
import type { CognitoUser } from "amazon-cognito-identity-js"

import { Amplify, Auth } from "aws-amplify"
import React from "react"
import { useForm, useFormContext } from "react-hook-form"

import { SEGMENT_CLIENT } from "@/analytics/analytics-client"
import { getUserPoolCredentials } from "@/base/aws-amplify"
import { ENVIRONMENT } from "@/base/environment"
import {
  loadActiveUserAsync,
  setNotificationChannelPreferenceAsync,
  updateUserAccountAsync,
} from "@/base/farmhq-api"
import { readLocalStorageAsync } from "@/base/async-storage"
import { makeFileLogger } from "@/base/logger"
import { makeValidator } from "@/base/type-guards"
import { COGNITO_ERROR_CODE, CognitoErrorMessage } from "@/constants/errors"
import { PASSWORD_RULES } from "@/constants/validation"
import i18n from "@/translations/i18n"
import { createSlice, isAnyOf, isRejected } from "@reduxjs/toolkit"
import * as Sentry from "@sentry/react-native"

import { createAsyncThunk } from "./create-async-thunk"

import type { RootState } from "./root.reducer"

import type { ValuesType } from "utility-types"

Amplify.configure({
  Auth: getUserPoolCredentials({
    targetDatabaseName: ENVIRONMENT.targetDatabaseName,
  }),
})

const fileLogger = makeFileLogger({ fileName: "auth.reducer" })

export interface AuthFormData {
  codeString: string
  confirmPassword: string
  email: string
  newPassword: string
  password: string
}

export function useAuthForm(options?: {
  defaultValues?: Partial<AuthFormData>
}) {
  return useForm<AuthFormData>({
    defaultValues: {
      codeString: "",
      confirmPassword: "",
      email: "",
      newPassword: "",
      password: "",
      ...options?.defaultValues,
    },
  })
}
export function useAuthFormContext() {
  return useFormContext<AuthFormData>()
}

export type AuthPageId =
  | "confirmSignUp"
  | "contactUs"
  | "passwordReset"
  | "signIn"
  | "signOut"
  | "signUp"

// TODO: Enum these:

export const CognitoRequestNames = {
  changePassword: "changePassword",
  confirmSignUp: "confirmSignUp",
  currentSession: "currentSession",
  forgotPassword: "forgotPassword",
  forgotPasswordSubmit: "forgotPasswordSubmit",
  resendSignUp: "resendSignUp",
  signIn: "signIn",
  signOut: "signOut",
  signUp: "signUp",
} as const
export type CognitoRequestName = ValuesType<typeof CognitoRequestNames> &
  keyof typeof Auth

export const isKnownAuthErrorCode = makeValidator(
  Object.values(COGNITO_ERROR_CODE),
)

/**
 * Hide an email address
 * @example
 * // returns 'd**@farmhq.com'
 * maskEmailAddres('dan@farmhq.com')
 *
 */
export function maskEmailAddress(email?: string): string {
  try {
    if (typeof email === "string") {
      const [prefix, suffix] = email.split("@")
      if (typeof prefix === "string" && typeof suffix === "string") {
        const starsCount = prefix.length - 1
        const firstChar = prefix.slice(0, 1)
        return [
          firstChar,
          ...Array.from(Array(starsCount)).map(() => "*"),
          "@",
          suffix,
        ].join("")
      }
    }
  } catch (error) {
    fileLogger.error(error)
  }
  return "your email address"
}

export function useValidatePassword() {
  const rules = [
    PASSWORD_RULES.minlength,
    PASSWORD_RULES.lowercaseCount,
    PASSWORD_RULES.uppercaseCount,
    PASSWORD_RULES.numericCount,
    PASSWORD_RULES.specialCharacterCount,
  ]
  return (value: string) => {
    return rules.find((pattern) => {
      if (!pattern.value.test(value)) {
        return true
      }
      return false
    })?.message
  }
}
export function getErrorCodeFromAuthError(error: unknown): AuthErrorCode {
  const e = error as Partial<AuthError> | string | null | undefined
  if (typeof e !== "undefined" && e !== null) {
    let code: string | undefined
    if (typeof e === "string") {
      code = e
    } else if (typeof e === "object") {
      if (e.code) {
        code = e.code
      } else if (e.name) {
        code = e.name
      }
    }
    if (typeof code === "string") {
      // Cognito seems to have trailing spaces on at least one error
      code = code.trim()
      if (isKnownAuthErrorCode(code)) {
        return code
      }
    }
  }

  return "UNKNOWN"
}
export function getErrorMessageFromAuthErrorCode(
  code: AuthErrorCode | null | undefined,
) {
  if (typeof code === "string") {
    switch (code) {
      case "NetworkError":
      case "UNKNOWN": {
        return i18n.t("auth:anUnknownErrorOccurred")
      }
      case "CodeDeliveryFailureException": {
        return i18n.t("auth:CodeDeliveryFailureException")
      }
      case "CodeMismatchException": {
        return i18n.t("auth:CodeMismatchException")
      }
      case "ExpiredCodeException": {
        return i18n.t("auth:ExpiredCodeException")
      }
      case "InvalidParemeterException": {
        return i18n.t("auth:InvalidParemeterException")
      }
      case "LimitExceededException": {
        return i18n.t("auth:LimitExceededException")
      }
      case "NotAuthorizedException": {
        return i18n.t("auth:NotAuthorizedException")
      }
      case "UserNotConfirmedException": {
        return i18n.t("auth:UserNotConfirmedException")
      }
      case "UserNotFoundException": {
        return i18n.t("auth:UserNotFoundException")
      }
      case "UsernameExistsException": {
        return i18n.t("auth:UsernameExistsException")
      }
      default: {
        break
      }
    }
  }
  return undefined
}

function handleAuthError(e: unknown): void {
  const error = e as Partial<AuthError> | undefined
  const errorCode = error?.code ?? error?.name
  const errorMessage = error?.message
  if (errorCode) {
    if (errorMessage === CognitoErrorMessage.NO_CURRENT_USER) {
      return
    }
    if (errorMessage === CognitoErrorMessage.REFRESH_TOKEN_EXPIRED) {
      return
    }
    if (isKnownAuthErrorCode(errorCode)) {
      return
    }

    if (errorCode === "NetworkError") {
      return
    }
    Sentry.captureException(errorCode)
  }
}

export interface AuthSuccessPayload {
  authToken: string | undefined
  email: string | undefined
  sub: string | undefined
}

/**
 * Set the user for sentry and analytics
 */
function handleAuthSuccess({ email, sub, ...rest }: AuthSuccessPayload) {
  Sentry.setUser({
    email,
    id: sub,
    // TODO: Get IP for mobile with expo device
    ip_address: "{{auto}}",
    username: email,
  })

  // TODO: add admin info to user tracking

  SEGMENT_CLIENT.identify(sub, { email, username: email }).catch((error) => {
    Sentry.captureException(error, { tags: { attempted: "identify user" } })
  })

  return { email, sub, ...rest }
}

/**
 * Check current authentication status for user
 */
export const currentSessionAsync = createAsyncThunk(
  CognitoRequestNames.currentSession,
  async () => {
    try {
      const session = await Auth.currentSession()
      const idToken = session.getIdToken()
      const { email, sub } = idToken.decodePayload() as {
        [key: string]: string | undefined
      }
      fileLogger.debug(`currentSession ✅ - ${maskEmailAddress(email)}`)
      const authToken = idToken.getJwtToken()
      return handleAuthSuccess({ authToken, email, sub })
    } catch (error) {
      fileLogger.error(error)
      throw error
    }
  },
)

export interface SignInFormValues {
  email: string
  password: string
}
export const signInAsync = createAsyncThunk(
  CognitoRequestNames.signIn,
  async (values: SignInFormValues) => {
    const email = values.email.trim().toLocaleLowerCase()
    const password = values.password.trim()
    try {
      const response = (await Auth.signIn({
        password,
        username: email,
      })) as CognitoUser

      // TODO: Fix
      const idToken = response.getSignInUserSession()?.getIdToken()
      const { sub } = idToken?.decodePayload() as {
        [key: string]: string | undefined
      }
      const authToken = idToken?.getJwtToken()
      fileLogger.debug(`signIn ✅ - ${maskEmailAddress(email)}`)
      return handleAuthSuccess({ authToken, email, sub })
    } catch (error) {
      handleAuthError(error)
      throw error
    }
  },
)

export const signOutAsync = createAsyncThunk(
  CognitoRequestNames.signOut,
  async () => {
    await Auth.signOut().catch((error) => {
      handleAuthError(error)
      throw error
    })
  },
)

export interface SignUpFormValues {
  confirmPassword: string
  email: string
  nameFirst?: string
  nameLast?: string
  password: string
}

export const signUpAsync = createAsyncThunk(
  CognitoRequestNames.signUp,
  async (values: SignUpFormValues) => {
    const email = values.email.trim().toLocaleLowerCase()
    const password = values.password.trim()
    await Auth.signUp({ password, username: email }).catch((error) => {
      handleAuthError(error)
      throw error
    })
  },
)

export const confirmSignUpAsync = createAsyncThunk(
  CognitoRequestNames.confirmSignUp,
  async (values: { codeString: string; email: string }) => {
    const username = values.email.trim().toLocaleLowerCase()
    const confirmationCode = values.codeString.toLocaleUpperCase()
    await Auth.confirmSignUp(username, confirmationCode).catch((error) => {
      handleAuthError(error)
      throw error
    })
  },
)
export const resendSignUpAsync = createAsyncThunk(
  CognitoRequestNames.resendSignUp,
  async ({ email }: { email: string }) => {
    const username = email.trim().toLocaleLowerCase()
    await Auth.resendSignUp(username).catch((error) => {
      handleAuthError(error)
      throw error
    })
  },
)
export const forgotPasswordAsync = createAsyncThunk(
  CognitoRequestNames.forgotPassword,
  async ({ email }: { email: string }) => {
    if (!email) {
      throw new TypeError("Empty value received for email address")
    }
    const username = email.trim().toLocaleLowerCase()
    await Auth.forgotPassword(username).catch((error) => {
      handleAuthError(error)
      throw error
    })
  },
)

export interface ForgotPasswordParams {
  codeString: string
  email: string
  password: string
}
export const forgotPasswordSubmitAsync = createAsyncThunk(
  CognitoRequestNames.forgotPasswordSubmit,
  async (values: ForgotPasswordParams) => {
    const username = values.email.trim().toLocaleLowerCase()
    const confirmationCode = values.codeString.trim()
    const newPassword = values.password.trim()
    return await Auth.forgotPasswordSubmit(
      username,
      confirmationCode,
      newPassword,
    ).catch((error) => {
      handleAuthError(error)
      throw error
    })
  },
)

export const changePasswordAsync = createAsyncThunk(
  CognitoRequestNames.changePassword,
  async (values: { email: string; password: string; passwordNew: string }) => {
    const username = values.email.trim().toLocaleLowerCase()
    const passwordOld = values.password.trim()
    const passwordNew = values.passwordNew.trim()
    return await Auth.changePassword(username, passwordOld, passwordNew).catch(
      (error) => {
        handleAuthError(error)
        throw error
      },
    )
  },
)

interface AuthState {
  authStatus?: FetchStatus
  email?: string
  errorCode?: AuthErrorCode
  notificationChannelPreference?: Models.NotificationChannelPreference
  isAccountDeactivated?: boolean
  userId?: string
  userNameFamily?: Models.UserAccountData["nameFamily"]
  userNameGiven?: Models.UserAccountData["nameGiven"]
}

const initialState: AuthState = {}

const authSlice = createSlice({
  extraReducers: (builder) => {
    return builder
      .addCase(loadActiveUserAsync.fulfilled, (state, { payload }) => {
        state.userNameGiven = payload.userData.nameGiven
        state.userNameFamily = payload.userData.nameFamily
        state.notificationChannelPreference =
          payload.userData.notificationChannelPreference ?? "both"
        state.isAccountDeactivated = false
      })
      .addCase(readLocalStorageAsync.fulfilled, (state, { payload }) => {
        if (
          state.notificationChannelPreference === undefined &&
          payload.notificationChannelPreference !== null
        ) {
          state.notificationChannelPreference =
            payload.notificationChannelPreference
        }
      })
      .addCase(loadActiveUserAsync.rejected, (state, { error }) => {
        if (error.name === "AccountDeactivatedError") {
          state.isAccountDeactivated = true
        }
      })
      .addCase(updateUserAccountAsync.fulfilled, (state, { meta }) => {
        state.userNameGiven = meta.arg.nameGiven
        state.userNameFamily = meta.arg.nameFamily
      })
      .addCase(
        setNotificationChannelPreferenceAsync.fulfilled,
        (state, { payload }) => {
          state.notificationChannelPreference =
            payload.notificationChannelPreference
        },
      )
      .addCase(currentSessionAsync.pending, (state) => {
        state.authStatus = "pending"
      })
      .addMatcher(
        isAnyOf(currentSessionAsync.fulfilled, signInAsync.fulfilled),
        (state, action) => {
          state.authStatus = "fulfilled"
          state.email = action.payload.email
          state.userId = action.payload.sub
        },
      )
      .addMatcher(
        isAnyOf(
          currentSessionAsync.rejected,
          signInAsync.rejected,
          signOutAsync.fulfilled,
        ),
        (state) => {
          state.authStatus = "rejected"
          // console.log(state.authStatus)
          state.email = undefined
          state.userId = undefined
        },
      )
      .addMatcher(
        isAnyOf(
          changePasswordAsync.rejected,
          confirmSignUpAsync.rejected,
          forgotPasswordAsync.rejected,
          forgotPasswordSubmitAsync.rejected,
          resendSignUpAsync.rejected,
          signInAsync.rejected,
          signUpAsync.rejected,
        ),
        (state, { error }) => {
          const errorCode = error.code ?? error.name
          state.errorCode = errorCode as AuthErrorCode
        },
      )
      .addMatcher(isRejected, (state, action) => {
        let message: string | undefined
        fileLogger.debug(action)
        // Find error message in response.
        if (action.meta.rejectedWithValue) {
          if (typeof action.payload === "string") {
            message = action.payload
          } else if (
            typeof action.payload === "object" &&
            !Array.isArray(action.payload) &&
            action.payload !== null
          ) {
            const value = action.payload as { [key: string]: unknown }
            if ("message" in value && typeof value.message === "string") {
              message = value.message
            }
          }
        } else {
          message = action.error.message
        }

        // Either of these should log the user out
        if (
          message === CognitoErrorMessage.NO_CURRENT_USER ||
          message === CognitoErrorMessage.REFRESH_TOKEN_EXPIRED
        ) {
          state.authStatus = "rejected"
          state.email = undefined
        }
      })
  },
  initialState,
  name: "auth",
  reducers: {},
})

const auth: Reducer<typeof initialState> = authSlice.reducer

export default auth

export function getAuthStatusFromState(state: RootState) {
  return state.auth.authStatus
}

export function getIsUserAuthenticatedFromState(state: RootState) {
  return getAuthStatusFromState(state) === "fulfilled"
}

export function getNotificationChannelPreferenceFromState(state: RootState) {
  return state.auth.notificationChannelPreference ?? "both"
}

export function useAuthError() {
  const [errorCode, setErrorCode] = React.useState<AuthErrorCode>()
  return {
    formErrorMessage: getErrorMessageFromAuthErrorCode(errorCode),
    setCodeFromAuthError: (e: unknown) => {
      setErrorCode(getErrorCodeFromAuthError(e))
    },
  }
}
